import Typography from "@mui/material/Typography";
import React from "react";

import APCard, { APCardColor } from "../../components/cards/APCard";
import { toDollars } from "../../helpers/currencies";
import { formatDate } from "../../helpers/dates";
import { APDocument, APDocumentContentType } from "../../types/Document";

const valueTransformers: Partial<
  Record<keyof APDocument, (value: string) => string>
> = {
  date: formatDate,
  date2: formatDate,
};

const contentTypeImageMap: Record<APDocumentContentType, string> = {
  file: "/file.png",
  website: "/website.png",
  video: "/video.png",
};

const DocumentCard = ({
  document,
  onDownload,
  onOpenUrl,
  titleFields,
  bodyFields,
  expandFields,
  fieldLabels,
  color,
}: {
  document: APDocument;
  onDownload: (documentId: string, fileName: string) => void;
  onOpenUrl: (url: string) => void;
  titleFields: (keyof APDocument)[];
  bodyFields: (keyof APDocument)[][];
  expandFields: (keyof APDocument)[][];
  fieldLabels?: Partial<Record<keyof APDocument, string>>;
  color?: APCardColor;
}) => {
  const handleCardClick = () => {
    if (document.mediaType === "file" && document.fileName) {
      onDownload(document.id, document.fileName || "document");
    } else if (document.mediaType === "url" && document.url) {
      onOpenUrl(document.url);
    }
  };

  const renderDocumentField = (
    documentKeys: (keyof APDocument)[],
    isTitle?: boolean
  ) => {
    const [firstField, secondField] = documentKeys;
    const firstTransformer = firstField && valueTransformers[firstField];
    const secondTransformer = secondField && valueTransformers[secondField];

    const firstValue =
      firstField && document[firstField]
        ? firstTransformer
          ? firstTransformer(`${document[firstField]}`)
          : document[firstField]
        : null;
    const secondValue =
      secondField && document[secondField]
        ? secondTransformer
          ? secondTransformer(`${document[secondField]}`)
          : document[secondField]
        : null;
    const fieldLabel =
      firstField && fieldLabels ? fieldLabels[firstField] : null;

    const fieldText = `${firstValue || ""}${
      secondValue ? ` - ${secondValue}` : ""
    }${fieldLabel ? ` - ${fieldLabel}` : ""}`;
    return (
      <React.Fragment key={firstField}>
        {firstValue && (
          <Typography
            variant={isTitle ? "titleMedium" : "bodyMedium"}
            sx={{ whiteSpace: "pre-line" }}
          >
            {fieldText}
          </Typography>
        )}
      </React.Fragment>
    );
  };

  return (
    <APCard
      titleLeft={renderDocumentField(titleFields, true)}
      bodyContent={bodyFields.map((field) => renderDocumentField(field))}
      expandContent={expandFields.map((field) => renderDocumentField(field))}
      titleRight={
        document.amount && (
          <Typography variant="titleMedium">
            {toDollars(document.amount, 2)}
          </Typography>
        )
      }
      cardImage={contentTypeImageMap[document.contentType]}
      onCardClick={handleCardClick}
      color={color}
    />
  );
};

export default DocumentCard;
